import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';

import {
  AfriexAdminPermission,
  BusinessFeeNegotiationParty,
} from '@afriex/core/domain/enums';
import type {
  BusinessFeeNegotiation,
  BusinessFeeScheduleRow,
} from '@afriex/core/domain/types';
import {
  BusinessFeeNegotiationFilter,
  getNegotiationDisplay,
} from '@afriex/core/domain/types';
import {
  formatFeeAmount,
  getCorridorKey,
  groupFeeSchedule,
  hasAdminPermission,
} from '@afriex/core/utils';

import {Badge, Button} from '@/components';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import Tabs from '@/components/Tabs';
import {appPaths} from '@/config/appPaths';
import type {CorridorColumn} from '@/screens/Merchants/Fee/CorridorLineTable';
import CorridorLineTable from '@/screens/Merchants/Fee/CorridorLineTable';
import {merchantService} from '@/services/merchants';

import BusinessScopeNotice from '../BusinessScopeNotice';

type BusinessFeeScheduleTabProps = {
  businessId: string;
  businessName?: string;
  /** Test hook prefix; each rendered element namespaces under it. */
  dataCy?: string;
};

/**
 * The priced corridors the business sees in Settings › Pricing, read only.
 * Prices are edited on the Fees subtab.
 */
const BusinessFeeScheduleTab = ({
  businessId,
  businessName,
  dataCy,
}: BusinessFeeScheduleTabProps) => {
  const [rows, setRows] = useState<BusinessFeeScheduleRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // An open offer, so the schedule shows which corridors are being renegotiated.
  const [negotiation, setNegotiation] = useState<BusinessFeeNegotiation | null>(
    null,
  );

  useEffect(() => {
    if (!businessId) {
      return undefined;
    }
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- refetching when the admin switches enterprise; the loading flag is the point of this effect, and the settle path below is async
    setIsLoading(true);
    setError(null);
    // Another enterprise has its own offer, and its request may not answer.
    setNegotiation(null);

    // Only roles that may see negotiations ask for one; the schedule loads
    // either way.
    if (
      hasAdminPermission(
        localStorage.getItem('admin-role') ?? undefined,
        AfriexAdminPermission.BusinessFeeNegotiationRead,
      )
    ) {
      merchantService
        .getBusinessFeeNegotiation(businessId, controller.signal)
        .then(setNegotiation)
        .catch(() => {
          if (!controller.signal.aborted) {
            setNegotiation(null);
          }
        });
    }

    merchantService
      .getBusinessFeeSchedule(businessId, controller.signal)
      .then((schedule) => setRows(schedule.rows))
      .catch(() => {
        if (!controller.signal.aborted) {
          setError('Could not load the fee schedule for this enterprise.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [businessId]);

  // The latest negotiation may be closed; only an open one is "in progress".
  const openNegotiation =
    negotiation && getNegotiationDisplay(negotiation).isOpen
      ? negotiation
      : null;
  const standingRound =
    openNegotiation?.rounds?.[openNegotiation.rounds.length - 1];
  const proposedByCorridor = new Map(
    (standingRound?.lines ?? []).map((line) => [
      getCorridorKey(line.corridor),
      line.proposedAmount,
    ]),
  );
  const isAwaitingAfx =
    openNegotiation !== null &&
    getNegotiationDisplay(openNegotiation).waitingOn ===
      BusinessFeeNegotiationParty.Afriex;
  const requestParams = new URLSearchParams({
    merchantId: businessId,
    negotiationTab: isAwaitingAfx
      ? BusinessFeeNegotiationFilter.AwaitingAdmin
      : BusinessFeeNegotiationFilter.AwaitingBusiness,
    ...(openNegotiation?.id ? {negotiationId: openNegotiation.id} : {}),
  }).toString();
  const {groups} = groupFeeSchedule(rows);
  const label = businessName || 'this enterprise';

  const columns: CorridorColumn<BusinessFeeScheduleRow>[] = [
    {
      key: 'fee',
      header: 'Fee',
      className: 'w-40',
      render: (row) => {
        const asked = proposedByCorridor.get(getCorridorKey(row));
        return (
          <>
            {row.isCustom ? (
              <span className="mr-2 text-gray-400 line-through dark:text-gray-500">
                {formatFeeAmount(row.defaultAmount)}
              </span>
            ) : null}
            {formatFeeAmount(row.amount)}
            {row.isActive ? null : (
              <Badge colorScheme="gray" className="ml-2 normal-case">
                Inactive
              </Badge>
            )}
            {asked ? (
              <span className="block text-xs text-amber-700 dark:text-amber-400">
                asks {formatFeeAmount(asked)}
              </span>
            ) : null}
          </>
        );
      },
    },
    {
      key: 'request',
      header: 'Request',
      className: 'w-44',
      render: (row) =>
        proposedByCorridor.has(getCorridorKey(row)) ? (
          <Badge colorScheme="pending" className="normal-case">
            Negotiating
          </Badge>
        ) : null,
    },
  ];

  const renderCorridorTag = (row: BusinessFeeScheduleRow) =>
    row.isCustom ? (
      <Badge colorScheme="blue" className="normal-case">
        Custom
      </Badge>
    ) : null;

  const renderSchedule = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }
    if (error) {
      return (
        <p
          data-cy={dataCy ? `${dataCy}-error` : undefined}
          className="py-10 text-center text-gray-500 dark:text-gray-400"
        >
          {error}
        </p>
      );
    }
    if (!groups.length) {
      return (
        <p
          data-cy={dataCy ? `${dataCy}-empty` : undefined}
          className="py-10 text-center text-gray-500 dark:text-gray-400"
        >
          No fee schedule for {label} yet. Prices appear once its fee rules are
          set up.
        </p>
      );
    }
    return (
      <div data-cy={dataCy ? `${dataCy}-corridors` : undefined}>
        {openNegotiation && standingRound ? (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
            <span>
              <span className="font-medium">
                Pricing negotiation in progress.
              </span>{' '}
              {isAwaitingAfx ? 'Awaiting AFX.' : 'Awaiting the merchant.'}{' '}
              {standingRound.lines.length}
              {standingRound.lines.length === 1
                ? ' corridor is'
                : ' corridors are'}{' '}
              being renegotiated; nothing changes until it is accepted.
            </span>
            <Link
              to={`/${appPaths.merchants}/pricing-negotiations?${requestParams}`}
            >
              <Button
                variant="outline"
                colorScheme="cyan"
                className="text-xs px-3 py-1 h-8"
              >
                Open request
              </Button>
            </Link>
          </div>
        ) : null}

        <Tabs
          dataCy={dataCy ? `${dataCy}-groups` : undefined}
          tabs={groups.map((group) => ({
            id: group.kind.toLowerCase(),
            title: group.label,
            dataCy: dataCy
              ? `${dataCy}-group-${group.kind.toLowerCase()}`
              : undefined,
            content: (
              <div className="flex flex-col gap-4">
                {group.groups.map((channel) => (
                  <div key={channel.channel}>
                    <p className="pb-1 text-sm text-gray-500 dark:text-gray-400">
                      {channel.label}
                    </p>
                    <CorridorLineTable
                      rows={channel.rows}
                      getCorridor={(row) => row}
                      columns={columns}
                      showChannel={false}
                      equalWidths
                      renderCorridorTag={renderCorridorTag}
                    />
                  </div>
                ))}
              </div>
            ),
          }))}
        />
      </div>
    );
  };

  return (
    <section className="px-3 pt-3">
      <div data-cy={dataCy ? `${dataCy}-scope-notice` : undefined}>
        <BusinessScopeNotice
          lead="Showing the pricing"
          name={label}
          tail=" sees in Settings › Pricing, read only. Its own fees are edited on the Fees tab and organization default fees under Merchants > Fees."
        />
      </div>

      <div className="mt-5 pb-4">
        <h3
          data-cy={dataCy ? `${dataCy}-title` : undefined}
          className="font-semibold text-gray-900 dark:text-gray-100"
        >
          Fee schedule for {label}
        </h3>
      </div>

      {renderSchedule()}
    </section>
  );
};

export default BusinessFeeScheduleTab;
